import { useEffect, useState, Fragment, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { WarningOctagon, ArrowsClockwise, Trash, ArrowUpRight, CheckCircle } from "@phosphor-icons/react";

const TYPE_META = {
  excel_seed_collision: { bg: "#FEE2E2", fg: "#991B1B", label: "Duplicate (Excel seed)" },
  due_date_revision: { bg: "#FEF3C7", fg: "#92400E", label: "Due-date revision" },
  post_push_revision: { bg: "#E0E7FF", fg: "#3730A3", label: "Revision after ERP push" },
};

const SRC_META = {
  excel: { bg: "#FEE2E2", fg: "#991B1B" },
  "app→ERP": { bg: "#DCFCE7", fg: "#166534" },
  app: { bg: "#DBEAFE", fg: "#1E40AF" },
  erp: { bg: "#E5E7EB", fg: "#374151" },
};

const Badge = ({ meta, children, testid }) => (
  <span data-testid={testid} style={{
    background: meta.bg, color: meta.fg, fontSize: 11, fontWeight: 700,
    padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap",
  }}>{children}</span>
);

export default function POReviews() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(0);
  const [probePo, setProbePo] = useState("");
  const [probeBusy, setProbeBusy] = useState(false);
  const [probe, setProbe] = useState(null);
  const [probeErr, setProbeErr] = useState(null);
  const navigate = useNavigate();

  const runProbe = async () => {
    const po = probePo.trim();
    if (!po) return;
    setProbeBusy(true);
    setProbe(null);
    setProbeErr(null);
    try {
      const r = await api.get(`/po/review-queue/diagnose`, { params: { custpo: po } });
      setProbe(r.data);
    } catch (e) {
      setProbeErr(e.response?.data?.detail || `Request failed (HTTP ${e.response?.status || "?"})`);
    } finally { setProbeBusy(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    api.get("/po/review-queue")
      .then((r) => setData(r.data))
      .catch((e) => {
        setData(null);
        const status = e.response?.status;
        const detail = e.response?.data?.detail;
        setErr(detail || (status ? `Request failed (HTTP ${status})` : e.message) || "Failed to load the review queue");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const retireSeed = async (pono, custpo) => {
    if (!window.confirm(`Retire the Excel-seed copy of PO ${custpo} (#${pono})?\n\nThe app/ERP copy stays. This is reversible (a backup is kept).`)) return;
    setBusy(pono);
    try {
      const r = await api.post("/po/review-queue/retire-seed", { ms_pono: pono });
      toast.success(r.data.message || "Excel seed retired");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to retire the Excel seed");
    } finally { setBusy(0); }
  };

  const items = data?.items || [];
  const byType = data?.by_type || {};
  const diag = data?.diagnostic || null;

  return (
    <Layout breadcrumb={["PO Reviews"]}>
      <div className="jde-toolbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <WarningOctagon size={20} weight="fill" color="#B45309" />
          <span style={{ fontWeight: 800, fontSize: 16 }}>PO Review Queue</span>
          <span className="muted" style={{ fontSize: 12 }}>duplicates &amp; revisions that need a decision</span>
        </span>
        <button data-testid="po-reviews-refresh" className="jde-btn" onClick={load} disabled={loading}
          style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ArrowsClockwise size={14} /> Refresh
        </button>
      </div>

      <div style={{ display: "flex", gap: 10, margin: "12px 0", flexWrap: "wrap" }}>
        {Object.entries(TYPE_META).map(([k, m]) => (
          <div key={k} data-testid={`po-reviews-count-${k}`} style={{
            border: "1px solid #E2E8F0", borderRadius: 8, padding: "8px 14px", background: "#fff",
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: m.fg }}>{byType[k] || 0}</div>
            <div className="muted" style={{ fontSize: 11 }}>{m.label}</div>
          </div>
        ))}
      </div>

      {err && (
        <div data-testid="po-reviews-error" style={{
          border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#991B1B",
          borderRadius: 8, padding: "12px 16px", margin: "12px 0", fontSize: 13,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Couldn&apos;t load the review queue</div>
          <div style={{ fontFamily: "Inter, sans-serif", wordBreak: "break-word" }}>{String(err)}</div>
          <div style={{ marginTop: 6, opacity: 0.85 }}>
            If this is a timeout (504/502), the endpoint is taking too long on this dataset — share this
            message so it can be optimized. Otherwise it points to the real backend error.
          </div>
        </div>
      )}

      {loading ? (
        <div className="muted" style={{ padding: 24 }} data-testid="po-reviews-loading">Loading review queue…</div>
      ) : err ? null : items.length === 0 ? (
        <div data-testid="po-reviews-empty" style={{ padding: 32, textAlign: "center", color: "#166534" }}>
          <CheckCircle size={40} weight="fill" style={{ opacity: 0.8 }} />
          <div style={{ fontWeight: 700, marginTop: 8 }}>Nothing needs review</div>
          <div className="muted" style={{ fontSize: 13 }}>No duplicate or revised POs are open right now.</div>
        </div>
      ) : (
        <table className="jde-table" data-testid="po-reviews-table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Type</th>
              <th style={{ textAlign: "left" }}>Customer / PO / Item</th>
              <th style={{ textAlign: "left" }}>Copies</th>
              <th style={{ textAlign: "left" }}>What&apos;s needed</th>
              <th style={{ textAlign: "left" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const meta = TYPE_META[it.type] || { bg: "#E5E7EB", fg: "#374151", label: it.type };
              return (
                <tr key={i} data-testid={`po-review-row-${it.ms_pono || it.ms_custpo}-${i}`}>
                  <td><Badge meta={meta} testid={`po-review-type-${i}`}>{meta.label}</Badge></td>
                  <td>
                    <div style={{ fontWeight: 700 }}>{it.customer_name}</div>
                    <div className="muted" style={{ fontSize: 12 }}>PO {it.ms_custpo}{it.icode ? ` · ${it.icode}` : ""}</div>
                  </td>
                  <td>
                    {it.type === "post_push_revision" ? (
                      <div style={{ fontSize: 12 }}>
                        <div>PO #{it.ms_pono}{it.revision ? ` · REV ${it.revision}` : ""}</div>
                        {(it.changes || []).map((c, j) => (
                          <div key={j} className="muted" style={{ fontSize: 11 }}>{c}</div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        {(it.copies || []).map((c, j) => (
                          <div key={j} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                            <Badge meta={SRC_META[c.src] || SRC_META.erp}>{c.src}</Badge>
                            <span>#{c.ms_pono}</span>
                            <span className="muted">due {c.deliv_date || "—"}</span>
                            {c.closed ? (
                              <span className="muted">ord {c.qty_ordered} · <strong style={{ color: "#166534" }}>shipped/closed</strong></span>
                            ) : (
                              <span className="muted">ord {c.qty_ordered} / open {c.qty_open}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ maxWidth: 260 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: meta.fg }}>{it.needed}</span>
                  </td>
                  <td>
                    {it.type === "excel_seed_collision" ? (
                      (it.copies || []).filter((c) => c.src === "excel").map((c) => (
                        <button key={c.ms_pono} data-testid={`po-review-retire-${c.ms_pono}`}
                          className="jde-btn danger" disabled={busy === c.ms_pono}
                          onClick={() => retireSeed(c.ms_pono, it.ms_custpo)}
                          style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <Trash size={13} /> {busy === c.ms_pono ? "Retiring…" : `Retire seed #${c.ms_pono}`}
                        </button>
                      ))
                    ) : (
                      <button data-testid={`po-review-open-${it.ms_pono || i}`} className="jde-btn"
                        onClick={() => navigate(`/pos/${(it.copies && it.copies[0]?.ms_pono) || it.ms_pono}`)}
                        style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <ArrowUpRight size={13} /> Open PO to revise
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {diag && !err && (
        <div data-testid="po-reviews-diagnostic" style={{
          marginTop: 16, border: "1px solid #E2E8F0", background: "#F8FAFC", borderRadius: 8,
          padding: "10px 14px", fontSize: 12, color: "#475569",
        }}>
          <strong style={{ color: "#334155" }}>Scan diagnostic</strong>{" "}
          <span style={{ fontFamily: "Inter, sans-serif" }}>
            open docs scanned {diag.open_docs_scanned ?? "—"} · open line-groups {diag.open_line_groups ?? "—"} ·
            {" "}excel-open groups {diag.excel_open_groups ?? "—"} · excel-open customer POs {diag.excel_open_custpos ?? "—"} ·
            {" "}twin docs scanned {diag.twin_docs_scanned ?? "—"} · shipped/closed twins found {diag.shipped_twins_found ?? "—"} ·
            {" "}post-push flagged {diag.post_push_flagged ?? "—"}
          </span>
          {items.length === 0 && (
            <div style={{ marginTop: 6 }}>
              A queue of 0 here means no open Excel-seed collided with an app/ERP or shipped twin, and no
              nearby-date revisions were found in <em>this environment&apos;s</em> data. If Preview shows items
              but Production shows 0, compare these counts to spot the split-brain.
            </div>
          )}
        </div>
      )}
      <div data-testid="po-probe-box" style={{
        marginTop: 16, border: "1px solid #CBD5E1", background: "#fff", borderRadius: 8, padding: "14px 16px",
      }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Diagnose a PO (why isn&apos;t a duplicate showing?)</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input data-testid="po-probe-input" value={probePo} onChange={(e) => setProbePo(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runProbe()}
            placeholder="Customer PO # e.g. 0997737921"
            style={{ flex: "1 1 240px", border: "1px solid #CBD5E1", borderRadius: 6, padding: "8px 10px", fontSize: 13 }} />
          <button data-testid="po-probe-btn" className="jde-btn" onClick={runProbe} disabled={probeBusy || !probePo.trim()}>
            {probeBusy ? "Checking…" : "Diagnose"}
          </button>
        </div>
        {probeErr && (
          <div data-testid="po-probe-error" style={{ marginTop: 10, color: "#991B1B", fontSize: 12, fontFamily: "Inter, sans-serif" }}>{String(probeErr)}</div>
        )}
        {probe && (
          <div data-testid="po-probe-result" style={{ marginTop: 12, fontSize: 12 }}>
            <div style={{
              padding: "8px 12px", borderRadius: 6, marginBottom: 10,
              background: probe.shared_key_open?.length ? "#DCFCE7" : (probe.shared_key_any?.length ? "#FEF3C7" : "#FEE2E2"),
              color: "#334155", fontWeight: 600,
            }}>{probe.verdict}</div>
            <div className="muted" style={{ marginBottom: 8, fontFamily: "Inter, sans-serif" }}>
              found {probe.found} · excel {probe.excel_copies} · non-excel {probe.nonexcel_copies}
            </div>
            <table className="jde-table" style={{ width: "100%", fontSize: 11 }}>
              <thead><tr>
                <th style={{ textAlign: "left" }}>Src</th><th style={{ textAlign: "left" }}>PoNo</th>
                <th style={{ textAlign: "left" }}>pcode → norm</th><th style={{ textAlign: "left" }}>custPO → norm</th>
                <th style={{ textAlign: "left" }}>status</th><th style={{ textAlign: "left" }}>pending</th>
                <th style={{ textAlign: "left" }}>skipped by queue</th>
              </tr></thead>
              <tbody>
                {(probe.copies || []).map((c, i) => (
                  <tr key={i}>
                    <td>{c.src}</td><td>#{c.ms_pono}</td>
                    <td>{c.ms_pcode} → {c.norm_pcode}</td>
                    <td>{c.ms_custpo} → {c.norm_custpo}</td>
                    <td>{c.status}</td><td>{c.qty_pending_total}</td>
                    <td style={{ color: c.skipped_by_review_queue?.length ? "#991B1B" : "#166534" }}>
                      {c.skipped_by_review_queue?.length ? c.skipped_by_review_queue.join("; ") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 14, lineHeight: 1.5 }}>
        <strong>How to resolve:</strong> For a <em>duplicate</em>, retire the Excel-seed copy — the app/ERP copy is authoritative (reversible; a backup is kept).
        For a <em>due-date revision</em>, open the PO, correct the date on the app, and it syncs to the ERP.
      </div>
    </Layout>
  );
}
